import { useState, useEffect } from "react";
import { useAuth } from "../../../provider";
import * as api from "../../../Auth/auth";
import axios from "axios";
import PageProtectedOnboarding from "../../../components/Page/PageProtectedOnboarding";
import {
  Grid,
  Typography,
  Box,
  Paper,
  Divider,
  Stack,
  CircularProgress,
  IconButton,
  useTheme,
  Radio,
  RadioGroup,
  FormControlLabel,
  Checkbox,
} from "@mui/material";
import { pool } from "../../../constants";
import OnboardingSteps from "../../../components/Steps/OnboardingSteps";
import DiaLabDetails from "./DiaLabDetails";
import * as c from "../../../constants";
import ButtonsMainGoTo from "../../../components/Button/ButtonMainGoTo";
import { useTranslation } from "../../../language";
import { OnboardingDialog as CycleOnboardingDialog } from "../../CockpitWelcome/PeriodTracker/CyclePeriod/SectionCyclePeriod";
import { Route, Pill, Check } from "lucide-react";
import CustomDialog from "../../../components/Dialog/DiaCompMedium";
import MedicationPanel from "../../CockpitWelcome/Medication/MedicationPanel";

// Maps composite radio value → API payload
const RADIO_PAYLOADS = {
  cycletracker:     { profile: "cycletracker" },
  cycletracker_hbc: { profile: "cycletracker", status: "hormonal_birth_control" },
  perimenopause:    { profile: "perimenopause" },
  postmenopause:    { profile: "postmenopause" },
};

export default function WaitingResults() {
  const { state, handleLogout } = useAuth();
  const theme = useTheme();
  const { t, changeLanguage } = useTranslation();

  const [visits, setVisits] = useState([]);
  const [visitId, setVisitId] = useState(null);
  const [order, setOrder] = useState([]);
  const [labDetails, setLabDetails] = useState(null);
  const [subscription, setSubscription] = useState(null);
  const [loading, setLoading] = useState(true);
  const [coreOpen, setCoreOpen] = useState(false);
  const [coreOpen2, setCoreOpen2] = useState(false);
  const [femaleProfile, setFemaleProfile] = useState("");
  const [profileSaved, setProfileSaved] = useState(false);
  const [femaleProfileLoading, setFemaleProfileLoading] = useState(true);
  const [medSaved, setMedSaved] = useState(false);
  const [noMedication, setNoMedication] = useState(false);

  useEffect(() => {
    if (state.user?.language) {
      changeLanguage(state.user.language);
    }
  }, [state.user?.language]);

  const isFemale = state?.user?.gender === "F";
  const userId = state?.user?.id;

  // Fetch current lab visit
  useEffect(() => {
    if (!userId) return;

    const fetchVisits = async () => {
      try {
        const res = await axios.get(`${c.API_URL}/api/current-lab-visit/`);
        const data = res.data;
        if (data) {
          setVisits([data]);
          setLabDetails(data.laboratory ?? null);
          setVisitId(data.id ?? null);
        }
      } catch (err) {
        console.error("No existing lab visit found:", err);
      }
    };

    fetchVisits();
  }, [userId]);

  // Fetch order items — only runs once visitId is available
  useEffect(() => {
    if (!userId || !visitId) return;

    const fetchOrder = async () => {
      try {
        const res = await axios.get(
          `${c.API_URL}/api/orders/by-lab-visit/${visitId}`
        );
        const data = res.data;
        if (data) setOrder([data]);
      } catch (err) {
        console.error("No add-on order items found:", err);
      }
    };

    fetchOrder();
  }, [userId, visitId]);

  // Fetch Stripe subscription
  useEffect(() => {
    axios
      .get(`${c.API_URL}/api/profile/stripecustomer`)
      .then((res) => setSubscription(res.data))
      .catch((err) => console.error("Error fetching subscription:", err))
      .finally(() => setLoading(false));
  }, []);

  // Check for existing female profile record
  useEffect(() => {
    if (!isFemale) {
      setFemaleProfileLoading(false);
      return;
    }
    axios
      .get(`${c.API_URL}/api/female/profile`)
      .then((res) => {
        const profile = res.data?.profile;
        const status  = res.data?.status;
        if (profile) {
          const radioValue =
            profile === "cycletracker" && status === "hormonal_birth_control"
              ? "cycletracker_hbc"
              : profile;
          setFemaleProfile(radioValue);
          setProfileSaved(true);
        }
      })
      .catch(() => {}) // 404 = no record → show radio options
      .finally(() => setFemaleProfileLoading(false));
  }, [isFemale]);

  const handleLogoutapi = async () => {
    const response = await api.logout();
    await handleLogout(response);
  };

  const handleFemaleProfileConfirm = async () => {
    try {
      await axios.post(`${c.API_URL}/api/female/profile`, RADIO_PAYLOADS[femaleProfile]);
      if (femaleProfile === "cycletracker" || femaleProfile === "cycletracker_hbc") {
        setCoreOpen(true);
      } else {
        setProfileSaved(true);
      }
    } catch (err) {
      console.error("Failed to save female profile:", err);
    }
  };

  if (loading) return <CircularProgress />;
  if (!subscription) return <Typography>{t("onb_wait_no_data")}</Typography>;

  const { last_payment_status, product } = subscription;

  const firstOrder = order[0] ?? null;
  const hasOrderItems = firstOrder?.items?.length > 0;

  return (
    <PageProtectedOnboarding
      headerImage={pool}
      brandtext="Preventive Health Intelligence"
      headline={t("onb_subscr37")}
      content={
        <>
          {/* Onboarding Steps */}
          <Grid container sx={{ mt: 2, display: "flex", justifyContent: "center", alignItems: "center" }}>
            <Grid size={12} sx={{ width: '100%', maxWidth: 900, px: { xs: 1, sm: 2 }, display: "flex", justifyContent: "center", alignItems: "center" }}>
              <OnboardingSteps stepNumber={4} />
            </Grid>
          </Grid>

          {/* Female-only: Cycle Tracker */}
          {isFemale && !femaleProfileLoading && (
            <Grid container sx={{ mt: 4, justifyContent: "center" }}>
              <Grid
                size={{ xs: 12 }}
                sx={{ maxWidth: 600, mx: "auto", px: { xs: 3, sm: 0 }, display: "flex", flexDirection: "column", alignItems: "center" }}
              >
                <Typography variant="h6" fontWeight={600} sx={{ mb: 2 }}>
                  {t("onb_tracker_header")}
                </Typography>
                <Box sx={{ mb: 2, textAlign: "center" }}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 400 }}>
                    {t("onb_tracker2")}
                    {!profileSaved && <> {t("onb_tracker_select")}</>}
                  </Typography>
                </Box>

                {profileSaved ? (
                  <Typography variant="subtitle1" sx={{ fontWeight: 400, color: "success.main" }}>
                    {t("onb_tracker_done")}
                  </Typography>
                ) : (
                  <>
                    <RadioGroup
                      value={femaleProfile}
                      onChange={(e) => setFemaleProfile(e.target.value)}
                      sx={{ alignSelf: "flex-start", mb: 2 }}
                    >
                      <FormControlLabel value="cycletracker"     control={<Radio />} label={<Typography variant="subtitle1" sx={{ fontWeight: 400 }}>{t("onb_tracker_opt1")}</Typography>} />
                      <FormControlLabel value="cycletracker_hbc" control={<Radio />} label={<Typography variant="subtitle1" sx={{ fontWeight: 400 }}>{t("onb_tracker_opt3")}</Typography>} />
                      <FormControlLabel value="perimenopause"    control={<Radio />} label={<Typography variant="subtitle1" sx={{ fontWeight: 400 }}>{t("onb_tracker_opt4")}</Typography>} />
                      <FormControlLabel value="postmenopause"    control={<Radio />} label={<Typography variant="subtitle1" sx={{ fontWeight: 400 }}>{t("onb_tracker_opt2")}</Typography>} />
                    </RadioGroup>

                    {(femaleProfile === "cycletracker" || femaleProfile === "cycletracker_hbc") && (
                      <Box component="ul" sx={{ pl: 3, m: 0, mb: 2, alignSelf: "flex-start" }}>
                        <Typography component="li" variant="subtitle1" sx={{ fontWeight: 400 }}>{t("onb_tracker3")}</Typography>
                        <Typography component="li" variant="subtitle1" sx={{ fontWeight: 400 }}>{t("onb_tracker4")}</Typography>
                        <Typography component="li" variant="subtitle1" sx={{ fontWeight: 400 }}>{t("onb_tracker5")}</Typography>
                      </Box>
                    )}

                    <IconButton
                      size="large"
                      disabled={!femaleProfile}
                      onClick={handleFemaleProfileConfirm}
                      sx={{ display: "flex", flexDirection: "row", gap: 1.5, bgcolor: "primary.C100", borderRadius: 7, px: 4, py: 1.5, "&:hover": { bgcolor: "primary.C200" }, "&.Mui-disabled": { opacity: 0.4 } }}
                    >
                      {(femaleProfile === "cycletracker" || femaleProfile === "cycletracker_hbc")
                        ? <Route size={32} color={theme.palette.secondary.dark} />
                        : <Check size={32} color={theme.palette.secondary.dark} />
                      }
                      <Typography variant="caption" sx={{ color: theme.palette.secondary.dark, fontWeight: 500, letterSpacing: 0.3 }}>
                        {(femaleProfile === "cycletracker" || femaleProfile === "cycletracker_hbc") ? t("onb_tracker6") : t("onb_tracker_confirm")}
                      </Typography>
                    </IconButton>
                  </>
                )}
              </Grid>
            </Grid>
          )}

          {/* Medication List */}
          <Grid container sx={{ mt: 4, justifyContent: "center" }}>
            <Grid
              size={{ xs: 12 }}
              sx={{ maxWidth: 600, mx: "auto", px: { xs: 3, sm: 0 }, display: "flex", flexDirection: "column", alignItems: "center" }}
            >
              <Typography variant="h6" fontWeight={600} sx={{ mb: 2 }}>
                {t("onb_med_header")}
              </Typography>
              {!noMedication && (
                <>
                  <Box component="ul" sx={{ pl: 3, m: 0, mb: 2, alignSelf: "flex-start" }}>
                    <Typography component="li" variant="subtitle1" sx={{ fontWeight: 400 }}>{t("onb_med1")} {t("onb_med2")} {t("onb_med3")}</Typography>
                    <Typography component="li" variant="subtitle1" sx={{ fontWeight: 400 }}>{t("onb_med4")}</Typography>
                    <Typography component="li" variant="subtitle1" sx={{ fontWeight: 400 }}>{t("onb_med5")}</Typography>
                  </Box>
                  <IconButton
                    size="large"
                    onClick={() => setCoreOpen2(true)}
                    sx={{ display: "flex", flexDirection: "row", gap: 1.5, bgcolor: "primary.C100", borderRadius: 7, px: 4, py: 1.5, "&:hover": { bgcolor: "primary.C200" } }}
                  >
                    <Pill size={32} color={theme.palette.secondary.dark} />
                    <Typography variant="caption" sx={{ color: theme.palette.secondary.dark, fontWeight: 500, letterSpacing: 0.3 }}>
                      {t("onb_tracker7")}
                    </Typography>
                  </IconButton>
                  {medSaved && (
                    <Typography variant="subtitle1" sx={{ fontWeight: 400, color: "success.main", mt: 2 }}>
                      {t("onb_med_done")}
                    </Typography>
                  )}
                </>
              )}

              <FormControlLabel
                sx={{ mt: 2 }}
                control={
                  <Checkbox
                    checked={noMedication}
                    onChange={(e) => setNoMedication(e.target.checked)}
                  />
                }
                label={<Typography variant="subtitle1" sx={{ fontWeight: 400 }}>{t("onb_med_none")}</Typography>}
              />
            </Grid>
          </Grid>

          {/* Status explanation */}
          <Grid
            container
            sx={{ mt: 4, display: "flex", justifyContent: "center" }}
          >
            <Grid
              size={{ xs: 12 }}
              sx={{ maxWidth: 600, mx: "auto", px: { xs: 3, sm: 0 } }}
            >
              <Box component="ul" sx={{ pl: 3, m: 0, mb: 2 }}>
                <Typography component="li" variant="subtitle1" sx={{ fontWeight: 400 }}>{t("onb_subscr27")}</Typography>
                <Typography component="li" variant="subtitle1" sx={{ fontWeight: 400 }}>{t("onb_subscr28")}</Typography>
                <Typography component="li" variant="subtitle1" sx={{ fontWeight: 400 }}>{t("onb_subscr29")}</Typography>
              </Box>
            </Grid>
          </Grid>

          {/* Lab Visit Date and Time */}
          <Grid
            container
            sx={{ mt: 2, display: "flex", justifyContent: "center" }}
          >
            <Grid
              size={{ xs: 12 }}
              sx={{ maxWidth: 600, mx: "auto", px: { xs: 2, sm: 0 } }}
            >
              {visits.length > 0 ? (
                visits.map((visit) => (
                  <Paper
                    key={visit.id}
                    elevation={2}
                    sx={{
                      px: { xs: 2, sm: 4 },
                      py: { xs: 1.5, sm: 1 },
                      borderRadius: 3,
                      width: "100%",
                      display: "flex",
                      flexDirection: { xs: "column", sm: "row" },
                      alignItems: "center",
                      justifyContent: "space-between",
                      gap: 1,
                      bgcolor: "grey.C50",
                    }}
                  >
                    <Typography variant="body1" fontWeight={400}>
                      {visit.laboratory?.name ?? "—"}
                    </Typography>
                    <Box>
                      <DiaLabDetails labDetails={labDetails} />
                    </Box>
                    <Typography variant="body1" fontWeight={400}>
                      {t("onb_visit_date_label")} {visit.visit_date ?? "—"}
                    </Typography>
                    <Typography variant="body1" fontWeight={400}>
                      {t("onb_visit_time_label")}{" "}
                      {visit.visit_time ? visit.visit_time.slice(0, 5) : "—"}
                    </Typography>
                  </Paper>
                ))
              ) : (
                <Typography
                  variant="subtitle1"
                  textAlign="center"
                  sx={{ color: "text.secondary", mt: 1 }}
                >
                  {t("onb_no_visits")}
                </Typography>
              )}
            </Grid>
          </Grid>

          {/* Subscription heading */}
          <Grid
            container
            sx={{ mt: 4, display: "flex", justifyContent: "center" }}
          >
            <Grid size={{ xs: 12 }} sx={{ maxWidth: 600, mx: "auto", px: { xs: 3, sm: 0 } }}>
              <Typography
                variant="subtitle1"
                textAlign="center"
                sx={{ fontWeight: 400 }}
              >
                {t("onb_subscr30")}
              </Typography>
            </Grid>
          </Grid>

          {/* Subscription Info */}
          <Grid
            container
            sx={{ mt: 1, display: "flex", justifyContent: "center" }}
          >
            <Grid size={{ xs: 12 }} sx={{ maxWidth: 600, mx: "auto", px: { xs: 2, sm: 0 } }}>
              {product ? (
                <Paper
                  elevation={3}
                  sx={{
                    width: "100%",
                    mt: 2,
                    p: { xs: 2, sm: 3 },
                    borderRadius: 3,
                    bgcolor: "grey.C50",
                  }}
                >
                  <Typography
                    variant="subtitle1"
                    fontWeight={600}
                    textAlign="center"
                    sx={{ mb: 2 }}
                  >
                    {t("onb_subscr31")}
                  </Typography>
                  <Stack spacing={1.5}>
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        bgcolor: "grey.C10",
                        borderRadius: 2,
                        px: 2,
                        py: 1,
                        boxShadow: 1,
                      }}
                    >
                      <Typography variant="subtitle2" fontWeight={500}>
                        {product.name}
                      </Typography>
                      <Typography variant="subtitle2" fontWeight={500}>
                        {t("onb_wait_per_year")}&nbsp;&nbsp;€ {product.price_eur}
                      </Typography>
                    </Box>
                  </Stack>
                  <Typography
                    variant="body2"
                    textAlign="right"
                    sx={{
                      mt: 1,
                      fontWeight: 500,
                      color:
                        last_payment_status === "succeeded"
                          ? "inherit"
                          : "error.main",
                    }}
                  >
                    {last_payment_status === "succeeded"
                      ? t("onb_wait_payment_ok")
                      : t("onb_wait_payment_fail")}
                  </Typography>
                </Paper>
              ) : (
                <Typography
                  variant="subtitle1"
                  textAlign="center"
                  sx={{ color: "text.secondary", mt: 2 }}
                >
                  {t("onb_wait_no_membership")}
                </Typography>
              )}
            </Grid>
          </Grid>

          {/* Addon Info */}
          <Grid
            container
            sx={{ mt: 1, display: "flex", justifyContent: "center" }}
          >
            <Grid size={{ xs: 12 }} sx={{ maxWidth: 600, mx: "auto", px: { xs: 2, sm: 0 } }}>
              {hasOrderItems ? (
                <Paper
                  elevation={3}
                  sx={{
                    width: "100%",
                    mt: 1,
                    p: { xs: 2, sm: 3 },
                    borderRadius: 3,
                    bgcolor: "grey.C50",
                  }}
                >
                  <Typography
                    variant="subtitle1"
                    fontWeight={600}
                    textAlign="center"
                    sx={{ mb: 2 }}
                  >
                    {t("onb_subscr32")}
                  </Typography>
                  <Stack spacing={1.5}>
                    {firstOrder.items.map((item) => (
                      <Box
                        key={item.id}
                        sx={{
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "center",
                          bgcolor: "grey.C10",
                          borderRadius: 2,
                          px: 2,
                          py: 1,
                          boxShadow: 1,
                        }}
                      >
                        <Typography variant="subtitle2" fontWeight={500}>
                          {item.product_name?.name ?? t("onb_wait_unnamed_test")}
                        </Typography>
                        <Typography variant="subtitle2" fontWeight={500}>
                          €&nbsp;{(item.unit_price_cents / 100).toFixed(2)}
                        </Typography>
                      </Box>
                    ))}
                  </Stack>
                  <Divider sx={{ my: 2 }} />
                  <Typography
                    variant="subtitle2"
                    textAlign="right"
                    fontWeight={600}
                    sx={{ mt: 1 }}
                  >
                    {t("onb_wait_addon_total")} €&nbsp;
                    {(firstOrder.total_amount / 100).toFixed(2)}
                  </Typography>
                  <Typography
                    variant="body2"
                    textAlign="right"
                    sx={{
                      mt: 1,
                      fontWeight: 500,
                      color: firstOrder.paid ? "inherit" : "error.main",
                    }}
                  >
                    {firstOrder.paid ? t("onb_wait_payment_ok") : t("onb_wait_payment_fail")}
                  </Typography>
                </Paper>
              ) : (
                <Typography
                  variant="subtitle2"
                  textAlign="center"
                  sx={{ color: "text.secondary", mt: 2, fontWeight: 400 }}
                >
                  {t("onb_wait_no_addons")}
                </Typography>
              )}
            </Grid>
          </Grid>

          {/* Logout */}
          <Grid
            container
            sx={{ mt: 6, display: "flex", justifyContent: "center" }}
          >
            <Grid
              size={{ xs: 12 }}
              sx={{ maxWidth: 600, mx: "auto", px: { xs: 3, sm: 0 } }}
            >
              <Typography
                variant="subtitle1"
                textAlign="center"
                sx={{ mb: 2, fontWeight: 400 }}
              >
                {t("onb_subscr33")}
                <br />
                {t("onb_subscr34")}
              </Typography>
            </Grid>
          </Grid>
          <Grid
            container
            sx={{ display: "flex", justifyContent: "center" }}
          >
            <Grid
              size={{ xs: 12 }}
              sx={{ mb: 12, maxWidth: 600, mx: "auto", display: "flex", justifyContent: "center" }}
            >
              <ButtonsMainGoTo name="LOGOUT" to="/Login" onClick={handleLogoutapi} />
            </Grid>
          </Grid>

          {/* Cycle Tracker Dialog */}
          <CycleOnboardingDialog
            open={coreOpen}
            onClose={() => setCoreOpen(false)}
            onSuccess={() => setProfileSaved(true)}
          />

          {/* Medication Dialog */}
          <CustomDialog
            open={coreOpen2}
            onClose={() => { setCoreOpen2(false); setMedSaved(true); }}
            title={t("onb_tracker7")}
            content={
              <Grid container>
                <Grid size={12} sx={{ display: "flex", justifyContent: "center" }}>
                  <MedicationPanel />
                </Grid>
              </Grid>
            }
          />
        </>
      }
    />
  );
}
